import {useTranslation} from "hooks/useTranslation";
import type {Locale} from "react-js-cron";
import {Cron} from "react-js-cron";
import "react-js-cron/dist/styles.css";
import {useMemo} from "react";
import {AlertTriangle} from "lucide-react";
import {CronExpressionParser} from "cron-parser";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

interface ScheduleConfigurationProps {
  value?: string;
  onChange: (value: string) => void;
  label?: string;
  showLabel?: boolean;
  className?: string;
}

const ScheduleConfiguration: React.FC<ScheduleConfigurationProps> = ({
  value = "",
  onChange,
  label,
  showLabel = true,
  className = "",
}) => {
  const { t } = useTranslation();

  const cronLocale: Locale = useMemo(() => {
    const basePath = "agents.newAgent.cron";
    return {
      everyText: t(`${basePath}.everyText`),
      emptyMonths: t(`${basePath}.emptyMonths`),
      emptyMonthDays: t(`${basePath}.emptyMonthDays`),
      emptyMonthDaysShort: t(`${basePath}.emptyMonthDaysShort`),
      emptyWeekDays: t(`${basePath}.emptyWeekDays`),
      emptyWeekDaysShort: t(`${basePath}.emptyWeekDaysShort`),
      emptyHours: t(`${basePath}.emptyHours`),
      emptyMinutes: t(`${basePath}.emptyMinutes`),
      emptyMinutesForHourPeriod: t(`${basePath}.emptyMinutesForHourPeriod`),
      yearOption: t(`${basePath}.yearOption`),
      monthOption: t(`${basePath}.monthOption`),
      weekOption: t(`${basePath}.weekOption`),
      dayOption: t(`${basePath}.dayOption`),
      hourOption: t(`${basePath}.hourOption`),
      minuteOption: t(`${basePath}.minuteOption`),
      rebootOption: t(`${basePath}.rebootOption`),
      prefixPeriod: t(`${basePath}.prefixPeriod`),
      prefixMonths: t(`${basePath}.prefixMonths`),
      prefixMonthDays: t(`${basePath}.prefixMonthDays`),
      prefixWeekDays: t(`${basePath}.prefixWeekDays`),
      prefixWeekDaysForMonthAndYearPeriod: t(
        `${basePath}.prefixWeekDaysForMonthAndYearPeriod`
      ),
      prefixHours: t(`${basePath}.prefixHours`),
      prefixMinutes: t(`${basePath}.prefixMinutes`),
      prefixMinutesForHourPeriod: t(`${basePath}.prefixMinutesForHourPeriod`),
      suffixMinutesForHourPeriod: t(`${basePath}.suffixMinutesForHourPeriod`),
      errorInvalidCron: t(`${basePath}.errorInvalidCron`),
      clearButtonText: t(`${basePath}.clearButtonText`),
      weekDays: t(`${basePath}.weekDays`, { returnObjects: true }) as string[],
      months: t(`${basePath}.months`, { returnObjects: true }) as string[],
      altWeekDays: t(`${basePath}.altWeekDays`, {
        returnObjects: true,
      }) as string[],
      altMonths: t(`${basePath}.altMonths`, {
        returnObjects: true,
      }) as string[],
    };
  }, [t]);

  const displayLabel = label || t("agents.newAgent.schedule");

  const localTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      return "UTC";
    }
  }, []);

  const nextRunDate = useMemo(() => {
    if (!value || !value.trim()) {
      return null;
    }

    try {
      // Parse cron expression with UTC (since cron is stored as UTC)
      const interval = CronExpressionParser.parse(value.trim(), {
        tz: "UTC",
      });
      const nextDateUTC = interval.next().toDate();

      // Convert UTC date to local timezone for display
      // dayjs will automatically convert UTC to local timezone when formatting
      return dayjs(nextDateUTC);
    } catch (error) {
      // Invalid cron expression
      return null;
    }
  }, [value]);

  const formattedNextRunDate = useMemo(() => {
    if (!nextRunDate) {
      return null;
    }

    return nextRunDate.format("DD/MM/YYYY hh:mm A");
  }, [nextRunDate]);

  return (
    <div className={className || "mt-4"}>
      {showLabel && (
        <label className="mb-2 ml-3 text-sm font-bold text-navy-700 dark:text-white">
          {displayLabel}
        </label>
      )}
      <p className="mb-2 ml-3 flex items-center gap-1.5 text-xs font-medium text-yellow-600 dark:text-yellow-400">
        <AlertTriangle className="h-3.5 w-3.5" />
        {t("agents.newAgent.cron.utcWarning")}
      </p>
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-navy-800">
        <Cron value={value || ""} setValue={onChange} locale={cronLocale} />
        {formattedNextRunDate && (
          <div className="mt-4 text-sm text-gray-700 dark:text-gray-300">
            <span className="font-semibold">
              {t("agents.newAgent.cron.nextRunDate")}:
            </span>{" "}
            <span>
              {formattedNextRunDate} ({localTimezone})
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleConfiguration;
